import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined, InboxOutlined, PlusOutlined,
  ReloadOutlined, SearchOutlined, SyncOutlined, TeamOutlined,
} from '@ant-design/icons';
import {
  Button, Card, Descriptions, Input, Modal, Popconfirm, Select, Space, Tag, Tooltip, message,
} from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {FaHandHoldingMedical, FaUserPlus} from 'react-icons/fa';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {RQUpdateUser, RqDeleteUser, RqUserLookupAllRole} from '../../../../ReactQuery/apis';
import {selectAuth} from '../../../../Redux/selectors';

import EditUser from './EditUser/EditUser';
import {RegisterPatient} from './RegisterPatient';

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];
const ROLE = 'PATIENT';
// ─── Patient Detail Modal ─────────────────────────────────────────────────────
const PatientDetailModal = ({patient, onClose}) => (
  <Modal
    open={!!patient}
    onCancel={onClose}
    footer={null}
    title={(
      <span className={'text-primary-700 font-bold text-base'}>
        {patient?.fullName}
      </span>
    )}
    width={560}
    destroyOnHidden
  >
    {patient && (
      <Descriptions column={1} bordered size={'small'} className={'mb-2 mt-4'}>
        <Descriptions.Item label={'Email'}>{patient.email}</Descriptions.Item>
        <Descriptions.Item label={'Mobile Number'}>{patient.mobileNumber}</Descriptions.Item>
        <Descriptions.Item label={'Age'}>{patient.age}</Descriptions.Item>
        <Descriptions.Item label={'Gender'}>{patient.gender}</Descriptions.Item>
        <Descriptions.Item label={'Blood Group'}>{patient.bloodGroup || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Occupation'}>{patient.occupation || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Medical Name'}>{patient.medicalname || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Address'}>{patient.address || '—'}</Descriptions.Item>
        <Descriptions.Item label={'Status'}>
          <Tag color={patient.status === 'ACTIVE' ? 'green' : 'red'}>{patient.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Created On'}>
          {patient.createdOn ? new Date(patient.createdOn).toLocaleString() : '—'}
        </Descriptions.Item>
      </Descriptions>
    )}
  </Modal>
);

PatientDetailModal.propTypes = {
  patient: PropTypes.shape({
    fullName:     PropTypes.string,
    email:        PropTypes.string,
    mobileNumber: PropTypes.string,
    age:          PropTypes.string,
    gender:       PropTypes.string,
    bloodGroup:   PropTypes.string,
    occupation:   PropTypes.string,
    medicalname:  PropTypes.string,
    address:      PropTypes.string,
    status:       PropTypes.string,
    createdOn:    PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const PatientScreen = () => {
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [detailPatient,     setDetailPatient]     = useState(null);
  const [editPatient,       setEditPatient]       = useState(null);
  const [pendingDeleteId,   setPendingDeleteId]   = useState(null);
  
  const navigate = useNavigate();

  // ── Local client-side filter state ──
  const [searchText,   setSearchText]   = useState('');
  const [statusFilter, setStatusFilter] = useState([]);

  const auth  = useSelector(selectAuth);
  const token = auth?.tokenData;
  const role = useSelector(state => state.auth?.user?.role);
  const basePath = role ? `/${role.toLowerCase()}` : '';

  const {
    data: allPatients = [],
    isFetching: allLoading,
    refetch,
  } = RqUserLookupAllRole(ROLE, token);

  const deleteUser = RqDeleteUser({
    onSuccess: () => {
      message.success('Patient deleted successfully');
      refetch();
    },
    onError: err => message.error(
      err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to delete patient',
    ),
    onSettled: () => setPendingDeleteId(null),
  });

  const handleDelete = id => {
    setPendingDeleteId(id);
    deleteUser.mutate({id, token});
  };

  // ── Client-side filtering ────────────────────────────────────────────────
  const patients = useMemo(() => {
    let list = [...allPatients].sort(
      (a, b) => new Date(b.createdOn ?? 0) - new Date(a.createdOn ?? 0),
    );

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(
        p => p.fullName?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q),
      );
    }

    if (statusFilter.length > 0) {
      list = list.filter(p => statusFilter.includes(p.status));
    }

    return list;
  }, [allPatients, searchText, statusFilter]);

  const activeCount = useMemo(
    () => patients.filter(p => p.status === 'ACTIVE').length,
    [patients],
  );

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter([]);
  };

  const hasActiveFilter = searchText.trim() !== '' || statusFilter.length > 0;

  const handleRegisterSuccess = () => {
    message.success('Patient list refreshed');
    refetch();
  };

  const handleEditSuccess = () => {
    refetch();
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title:  'Sr. No.',
      key:    'srno',
      width:  70,
      render: (_, __, index) => index + 1,
    },
    {
      title:     'Full Name',
      dataIndex: 'fullName',
      sorter:    (a, b) => (a.fullName || '').localeCompare(b.fullName || ''),
      render:    (name, rec) => (
        <button
          onClick={() => setDetailPatient(rec)}
          className={'hover:text-primary-800 font-semibold text-base text-left hover:underline bg-transparent border-0 cursor-pointer p-0'}
        >
          {name}
        </button>
      ),
    },
    {title: 'Email', dataIndex: 'email'},
    {title: 'Mobile Number', dataIndex: 'mobileNumber'},
    {title: 'Age', dataIndex: 'age', sorter: (a, b) => Number(a.age) - Number(b.age)},
    {title: 'Gender', dataIndex: 'gender'},
    {
      title:  'Blood Group',
      dataIndex: 'bloodGroup',
      render: v => v || <span className={'text-gray-400'}>—</span>,
    },
    {
      title:     'Status',
      dataIndex: 'status',
      render:    s => <Tag color={s === 'ACTIVE' ? 'blue' : 'default'} className={'font-medium'}>{s}</Tag>,
    },
    {
      title:            'Created On',
      dataIndex:        'createdOn',
      defaultSortOrder: 'descend',
      sorter:           (a, b) => new Date(a.createdOn) - new Date(b.createdOn),
      render:           d => (d ? new Date(d).toLocaleDateString() : '—'),
    },
    {
      title:  'Actions',
      key:    'actions',
      width:  150,
      render: (_, rec) => {
        const isDeletingThisRow = pendingDeleteId === rec.id && deleteUser.isPending;

        return (
          <Space size={4} onClick={e => e.stopPropagation()}>
          
            <Tooltip title={'Edit'}>
              <Button
                size={'small'}
                icon={<EditOutlined />}
                onClick={() => setEditPatient(rec)}
                disabled={pendingDeleteId === rec.id}
                className={'text-primary-600 border-primary-200 hover:border-primary-400'}
              />
            </Tooltip>
            <Tooltip title={'Delete'}>
              <Popconfirm
                title={'Delete this patient?'}
                description={'This action cannot be undone.'}
                onConfirm={() => handleDelete(rec.id)}
                okButtonProps={{danger: true, loading: isDeletingThisRow}}
                okText={'Delete'}
              >
                <Button
                  size={'small'}
                  icon={<DeleteOutlined />}
                  danger
                  loading={isDeletingThisRow}
                  disabled={pendingDeleteId === rec.id && !isDeletingThisRow}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={'min-h-screen md:p-6 p-2'}>

      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #b69c66, #b69c66)'}}
          >
            <FaHandHoldingMedical  style={{fontSize: 22}} />
          </div>
          <h1 className={'title !mb-0'}>Patients</h1>
        </div>

        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={allLoading}
            className={'btn-secondary'}
          >
            Refresh
          </Button>
          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            onClick={() => setRegisterModalOpen(true)}
            className={'btn-primary'}
          >
            Register Patient
          </Button>
        </Space>
      </div>

      {/* Stat Cards */}
      <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6'}>
        <StatCard icon={<FaUserPlus />}    label={'Total Patients'}  value={patients.length} colorClass={'bg-white'}      />
        <StatCard icon={<InboxOutlined />} label={'Active Patients'} value={activeCount}      colorClass={'bg-primary-50'} />
      </div>

      {/* ── Filter Bar ── */}
      <div className={'mb-5 '}>
        <div className={'flex items-center gap-2 mb-3'}>
          <FilterOutlined className={'text-primary-500'} />
          <span className={'font-semibold text-gray-700 text-sm'}>Filters</span>
          {hasActiveFilter && (
            <Tag color={'blue'} className={'ml-1 text-xs'}>
              {patients.length} result{patients.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </div>

        <div className={'flex flex-wrap gap-3 items-center'}>
          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by name or email…'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{width: 280}}
          />

          <Select
            mode={'multiple'}
            placeholder={'Filter by status'}
            value={statusFilter}
            onChange={val => setStatusFilter(val)}
            style={{minWidth: 200}}
            options={STATUS_OPTIONS.map(s => ({
              value: s,
              label: (
                <Tag color={s === 'ACTIVE' ? 'blue' : 'default'} className={'text-xs my-0'}>
                  {s}
                </Tag>
              ),
            }))}
            allowClear
            onClear={() => setStatusFilter([])}
          />

          {hasActiveFilter && (
            <Button icon={<SyncOutlined />} onClick={handleClearFilters}>
              Reset
            </Button>
          )}
        </div>
      </div>
      {/* Table */}
      <Card
        className={'rounded-lg border border-primary-100 shadow-sm'}
        styles={{body: {padding: 0}}}

      >
        <TableData
          dataSource={patients}
          columns={columns}
          rowKey={'id'}
          loading={allLoading}
          size={'middle'}
          scroll={{x: 900}}
          onRow={record => ({
            onClick: () =>
              navigate(`${basePath}/patients/${record.id}`, {
                state: {patient: record},
              }),
            style: {cursor: 'pointer'},
          })}
          
          rowClassName={'hover:bg-primary-50 transition-colors'}
        />
      </Card>

      <RegisterPatient
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        onSuccess={handleRegisterSuccess}
      />

      <PatientDetailModal
        patient={detailPatient}
        onClose={() => setDetailPatient(null)}
      />

      <EditUser
        open={!!editPatient}
        onClose={() => setEditPatient(null)}
        onSuccess={handleEditSuccess}
        user={editPatient}
        token={token}
      />
    </div>
  );
};

export default PatientScreen;
